/**
 * PatternEditor Component
 *
 * Editable list of regex patterns per intent type with real-time validation,
 * enable/disable toggles, and priority reordering.
 *
 * Validates: Requirements 6.1, 6.2, 6.3, 6.4, 6.5, 6.6
 * - Requirement 6.1: THE Console SHALL provide an editable interface for each intent type's regex patterns.
 * - Requirement 6.2: THE Console SHALL allow adding, removing, and modifying individual regex patterns.
 * - Requirement 6.3: THE Console SHALL allow enabling or disabling individual intent types.
 * - Requirement 6.4: THE Console SHALL validate regex syntax in real-time as the operator types.
 * - Requirement 6.5: IF a pattern has invalid regex syntax, THEN THE Console SHALL highlight the error.
 * - Requirement 6.6: THE Console SHALL allow reordering intent priorities via numeric input.
 */

import { useCallback, useMemo } from 'react';
import { Button, Input, Card, Badge } from '../../design-system';
import { tokens } from '../../design-system/tokens';

// ============================================
// TYPES
// ============================================

export interface IntentPatternDef {
  patterns: string[];
  priority: number;
  confidenceBoost: number;
  enabled: boolean;
}

export interface IntentConfig {
  version: string;
  generatedAt: string;
  patterns: Record<string, IntentPatternDef>;
}

export interface PatternEditorProps {
  /** The current intent configuration to edit */
  intentConfig: IntentConfig;
  /** Callback when any change is made to the config */
  onChange: (config: IntentConfig) => void;
  /** Whether the editor is disabled (e.g., during save) */
  disabled?: boolean;
}

// ============================================
// CONSTANTS
// ============================================

const MAX_PATTERNS_PER_INTENT = 20;

// ============================================
// HELPERS
// ============================================

/**
 * Validates a regex pattern string by attempting to construct a RegExp.
 * Returns null if valid, or an error message if invalid.
 */
function validateRegex(pattern: string): string | null {
  if (!pattern.trim()) {
    return 'El patrón no puede estar vacío';
  }
  try {
    new RegExp(pattern);
    return null;
  } catch (e) {
    return (e as Error).message;
  }
}

// ============================================
// ICONS
// ============================================

function IconPlus() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <path d="M12 5v14M5 12h14" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconTrash() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden>
      <polyline points="3,6 5,6 21,6" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

// ============================================
// SUB-COMPONENTS
// ============================================

interface PatternRowProps {
  pattern: string;
  index: number;
  intentType: string;
  disabled?: boolean;
  onChangePattern: (intentType: string, index: number, value: string) => void;
  onRemovePattern: (intentType: string, index: number) => void;
}

function PatternRow({
  pattern,
  index,
  intentType,
  disabled,
  onChangePattern,
  onRemovePattern,
}: PatternRowProps) {
  const error = useMemo(() => validateRegex(pattern), [pattern]);

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: tokens.spacing[2],
        marginBottom: tokens.spacing[2],
      }}
    >
      <div style={{ flex: 1 }}>
        <Input
          value={pattern}
          onChange={(e) => onChangePattern(intentType, index, e.target.value)}
          error={error || undefined}
          disabled={disabled}
          size="sm"
          placeholder="\\b(palabra|word)\\b"
          aria-label={`Patrón ${index + 1} para ${intentType}`}
          style={{ fontFamily: tokens.typography.fontFamily.mono }}
        />
      </div>
      <Button
        variant="ghost"
        size="sm"
        onClick={() => onRemovePattern(intentType, index)}
        disabled={disabled}
        aria-label={`Eliminar patrón ${index + 1} de ${intentType}`}
        style={{ marginTop: tokens.spacing[1], color: tokens.colors.error.main }}
      >
        <IconTrash />
      </Button>
    </div>
  );
}

interface IntentTypeSectionProps {
  intentType: string;
  intentDef: IntentPatternDef;
  disabled?: boolean;
  onChangePattern: (intentType: string, index: number, value: string) => void;
  onRemovePattern: (intentType: string, index: number) => void;
  onAddPattern: (intentType: string) => void;
  onToggleEnabled: (intentType: string) => void;
  onChangePriority: (intentType: string, priority: number) => void;
}

function IntentTypeSection({
  intentType,
  intentDef,
  disabled,
  onChangePattern,
  onRemovePattern,
  onAddPattern,
  onToggleEnabled,
  onChangePriority,
}: IntentTypeSectionProps) {
  const canAddMore = intentDef.patterns.length < MAX_PATTERNS_PER_INTENT;

  return (
    <div
      style={{
        marginBottom: tokens.spacing[4],
        padding: tokens.spacing[4],
        borderRadius: tokens.borderRadius.md,
        border: `1px solid ${tokens.colors.neutral[200]}`,
        backgroundColor: intentDef.enabled ? tokens.colors.neutral[50] : tokens.colors.neutral[100],
        opacity: intentDef.enabled ? 1 : 0.7,
        transition: `all ${tokens.transitions.normal}`,
      }}
    >
      {/* Intent type header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: tokens.spacing[3],
          flexWrap: 'wrap',
          gap: tokens.spacing[2],
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacing[2] }}>
          <h4
            style={{
              margin: 0,
              fontSize: tokens.typography.fontSize.sm,
              fontWeight: tokens.typography.fontWeight.semibold,
              color: tokens.colors.neutral[800],
              fontFamily: tokens.typography.fontFamily.mono,
            }}
          >
            {intentType}
          </h4>
          <Badge
            variant={intentDef.enabled ? 'success' : 'neutral'}
            size="sm"
          >
            {intentDef.enabled ? 'Activo' : 'Inactivo'}
          </Badge>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacing[3] }}>
          {/* Priority input */}
          <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacing[1] }}>
            <label
              htmlFor={`priority-${intentType}`}
              style={{
                fontSize: tokens.typography.fontSize.xs,
                color: tokens.colors.neutral[500],
                whiteSpace: 'nowrap',
              }}
            >
              Prioridad:
            </label>
            <input
              id={`priority-${intentType}`}
              type="number"
              min={1}
              max={99}
              value={intentDef.priority}
              onChange={(e) => {
                const val = parseInt(e.target.value, 10);
                if (!isNaN(val) && val >= 1 && val <= 99) {
                  onChangePriority(intentType, val);
                }
              }}
              disabled={disabled}
              aria-label={`Prioridad de ${intentType}`}
              style={{
                width: '56px',
                padding: `${tokens.spacing[1]} ${tokens.spacing[2]}`,
                fontSize: tokens.typography.fontSize.sm,
                fontFamily: tokens.typography.fontFamily.mono,
                border: `1px solid ${tokens.colors.neutral[300]}`,
                borderRadius: tokens.borderRadius.sm,
                textAlign: 'center',
                backgroundColor: tokens.colors.neutral[50],
                color: tokens.colors.neutral[900],
              }}
            />
          </div>

          {/* Enable/disable toggle */}
          <button
            type="button"
            role="switch"
            aria-checked={intentDef.enabled}
            aria-label={`${intentDef.enabled ? 'Desactivar' : 'Activar'} ${intentType}`}
            onClick={() => onToggleEnabled(intentType)}
            disabled={disabled}
            style={{
              position: 'relative',
              width: '40px',
              height: '22px',
              borderRadius: tokens.borderRadius.full,
              border: 'none',
              backgroundColor: intentDef.enabled
                ? tokens.colors.primary[600]
                : tokens.colors.neutral[300],
              cursor: disabled ? 'not-allowed' : 'pointer',
              transition: `background-color ${tokens.transitions.fast}`,
              padding: 0,
            }}
          >
            <span
              style={{
                position: 'absolute',
                top: '2px',
                left: intentDef.enabled ? '20px' : '2px',
                width: '18px',
                height: '18px',
                borderRadius: tokens.borderRadius.full,
                backgroundColor: '#ffffff',
                transition: `left ${tokens.transitions.fast}`,
                boxShadow: tokens.shadows.sm,
              }}
            />
          </button>
        </div>
      </div>

      {/* Pattern list */}
      <div style={{ marginBottom: tokens.spacing[2] }}>
        {intentDef.patterns.map((pattern, index) => (
          <PatternRow
            key={`${intentType}-${index}`}
            pattern={pattern}
            index={index}
            intentType={intentType}
            disabled={disabled || !intentDef.enabled}
            onChangePattern={onChangePattern}
            onRemovePattern={onRemovePattern}
          />
        ))}
      </div>

      {/* Add pattern button */}
      {canAddMore && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onAddPattern(intentType)}
          disabled={disabled || !intentDef.enabled}
          leftIcon={<IconPlus />}
          aria-label={`Agregar patrón a ${intentType}`}
        >
          Agregar patrón
        </Button>
      )}
      {!canAddMore && (
        <p
          style={{
            margin: 0,
            fontSize: tokens.typography.fontSize.xs,
            color: tokens.colors.warning.dark,
          }}
        >
          Máximo {MAX_PATTERNS_PER_INTENT} patrones alcanzado
        </p>
      )}
    </div>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

/**
 * PatternEditor — Editable list of regex patterns per intent type.
 *
 * Displays each intent type as a section with:
 * - Enable/disable toggle
 * - Priority numeric input
 * - List of editable regex patterns with real-time validation
 * - Add/remove pattern buttons
 *
 * @example
 * ```tsx
 * <PatternEditor
 *   intentConfig={config}
 *   onChange={(newConfig) => setConfig(newConfig)}
 * />
 * ```
 */
export function PatternEditor({ intentConfig, onChange, disabled }: PatternEditorProps) {
  // Sort intent types by priority for display
  const sortedIntentTypes = useMemo(() => {
    return Object.entries(intentConfig.patterns).sort(
      ([, a], [, b]) => a.priority - b.priority
    );
  }, [intentConfig.patterns]);

  const handleChangePattern = useCallback(
    (intentType: string, index: number, value: string) => {
      const updated = { ...intentConfig };
      const patterns = { ...updated.patterns };
      const intentDef = { ...patterns[intentType] };
      const patternList = [...intentDef.patterns];
      patternList[index] = value;
      intentDef.patterns = patternList;
      patterns[intentType] = intentDef;
      updated.patterns = patterns;
      onChange(updated);
    },
    [intentConfig, onChange]
  );

  const handleRemovePattern = useCallback(
    (intentType: string, index: number) => {
      const updated = { ...intentConfig };
      const patterns = { ...updated.patterns };
      const intentDef = { ...patterns[intentType] };
      const patternList = [...intentDef.patterns];
      patternList.splice(index, 1);
      intentDef.patterns = patternList;
      patterns[intentType] = intentDef;
      updated.patterns = patterns;
      onChange(updated);
    },
    [intentConfig, onChange]
  );

  const handleAddPattern = useCallback(
    (intentType: string) => {
      const updated = { ...intentConfig };
      const patterns = { ...updated.patterns };
      const intentDef = { ...patterns[intentType] };
      const patternList = [...intentDef.patterns, ''];
      intentDef.patterns = patternList;
      patterns[intentType] = intentDef;
      updated.patterns = patterns;
      onChange(updated);
    },
    [intentConfig, onChange]
  );

  const handleToggleEnabled = useCallback(
    (intentType: string) => {
      const updated = { ...intentConfig };
      const patterns = { ...updated.patterns };
      const intentDef = { ...patterns[intentType] };
      intentDef.enabled = !intentDef.enabled;
      patterns[intentType] = intentDef;
      updated.patterns = patterns;
      onChange(updated);
    },
    [intentConfig, onChange]
  );

  const handleChangePriority = useCallback(
    (intentType: string, priority: number) => {
      const updated = { ...intentConfig };
      const patterns = { ...updated.patterns };
      const intentDef = { ...patterns[intentType] };
      intentDef.priority = priority;
      patterns[intentType] = intentDef;
      updated.patterns = patterns;
      onChange(updated);
    },
    [intentConfig, onChange]
  );

  if (sortedIntentTypes.length === 0) {
    return (
      <Card variant="outlined" padding="md">
        <p
          style={{
            margin: 0,
            textAlign: 'center',
            color: tokens.colors.neutral[500],
            fontSize: tokens.typography.fontSize.sm,
          }}
        >
          No hay reglas de detección configuradas. Active el plugin para generarlas automáticamente.
        </p>
      </Card>
    );
  }

  return (
    <div role="region" aria-label="Detección de intenciones del usuario">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: tokens.spacing[4],
        }}
      >
        <Badge variant="info" size="sm">
          v{intentConfig.version}
        </Badge>
      </div>

      <p
        style={{
          margin: 0,
          marginBottom: tokens.spacing[4],
          fontSize: tokens.typography.fontSize.sm,
          color: tokens.colors.neutral[500],
          lineHeight: tokens.typography.lineHeight.normal,
        }}
      >
        Estas expresiones determinan cuándo el plugin se activa según lo que escribe el usuario. Cada acción tiene palabras clave que la disparan. Se evalúan en orden de prioridad (menor número = mayor prioridad).
      </p>

      {sortedIntentTypes.map(([intentType, intentDef]) => (
        <IntentTypeSection
          key={intentType}
          intentType={intentType}
          intentDef={intentDef}
          disabled={disabled}
          onChangePattern={handleChangePattern}
          onRemovePattern={handleRemovePattern}
          onAddPattern={handleAddPattern}
          onToggleEnabled={handleToggleEnabled}
          onChangePriority={handleChangePriority}
        />
      ))}
    </div>
  );
}

export default PatternEditor;
